/**
 * Generatory JSON-LD schema.org dla bogatych wyników w Google.
 * Używać przez <script type="application/ld+json"> w komponencie.
 */
import { SITE_URL, SITE_NAME, absUrl } from "./seo";

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/og-default.jpg`,
    description:
      "SyncTech — integracje e-commerce, wdrożenia Baselinker, Shopify, Shoper, ShopGold, SellAsist, PrestaShop. Automatyzacje, kampanie reklamowe, marketing.",
    areaServed: "PL",
    sameAs: [
      // TODO: dodaj realne profile gdy powstaną
      // "https://www.linkedin.com/company/synctech",
      // "https://www.facebook.com/synctech",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      areaServed: "PL",
      availableLanguage: ["Polish", "English"],
    },
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: SITE_NAME,
    inLanguage: "pl-PL",
    publisher: { "@id": `${SITE_URL}/#organization` },
  };
}

export function professionalServiceSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${SITE_URL}/#service`,
    name: SITE_NAME,
    url: SITE_URL,
    image: `${SITE_URL}/og-default.jpg`,
    priceRange: "$$",
    areaServed: { "@type": "Country", name: "Polska" },
    serviceType: [
      "Integracje e-commerce",
      "Wdrożenia Baselinker",
      "Wdrożenia Shopify",
      "Wdrożenia Shoper",
      "Wdrożenia SellAsist",
      "Wdrożenia ShopGold",
      "Wdrożenia PrestaShop",
      "Kampanie Google Ads",
      "Kampanie Facebook Ads",
      "Email marketing automation",
    ],
  };
}

export function breadcrumbSchema(items: Array<{ name: string; path: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: absUrl(item.path),
    })),
  };
}

export function serviceSchema(input: {
  name: string;
  description: string;
  path: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: input.name,
    description: input.description,
    url: absUrl(input.path),
    provider: { "@id": `${SITE_URL}/#organization` },
    areaServed: { "@type": "Country", name: "Polska" },
  };
}

export function faqPageSchema(faqs: Array<{ q: string; a: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function articleSchema(input: {
  title: string;
  description: string;
  path: string;
  datePublished: string;
  image?: string;
  keywords?: string[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: input.title,
    description: input.description,
    image: input.image ?? `${SITE_URL}/og-default.jpg`,
    datePublished: input.datePublished,
    dateModified: input.datePublished,
    author: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
    publisher: { "@id": `${SITE_URL}/#organization` },
    mainEntityOfPage: { "@type": "WebPage", "@id": absUrl(input.path) },
    keywords: (input.keywords ?? []).join(", "),
    inLanguage: "pl-PL",
  };
}

/** Helper do renderowania JSON-LD jako prop dangerouslySetInnerHTML */
export function jsonLd(data: unknown) {
  return { __html: JSON.stringify(data) };
}
